import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Group, InlineError, Paper, Stack, Text } from '@wapharma/design-system';
import { apiGetList } from '../../apiList';
import { getDisplayError } from '../../apiErrors';
import { storefrontBaseUrl } from '../../lib/storefrontPublicUrl';

type FeaturedProduct = {
  id: string;
  name: string;
  slug?: string | null;
};

export function StorefrontFeaturedProductsPanel({
  disabled,
  onChanged,
}: {
  disabled?: boolean;
  onChanged?: () => void;
}) {
  const [items, setItems] = useState<FeaturedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: rows } = await apiGetList<FeaturedProduct>(
        '/catalog/products?featured=true&limit=24&page=1&sortBy=name&sortOrder=asc',
      );
      setItems(rows);
    } catch (e) {
      setError(getDisplayError(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap="md">
      {error ? <InlineError>{error}</InlineError> : null}
      <Group gap="sm" wrap="wrap">
        <Button
          type="button"
          variant="soft"
          onClick={() => {
            void load().then(() => onChanged?.());
          }}
          disabled={disabled || loading}
        >
          {loading ? 'Chargement…' : 'Actualiser'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => window.open(storefrontBaseUrl(), '_blank', 'noopener,noreferrer')}
        >
          Ouvrir la boutique
        </Button>
        <Button type="button" variant="ghost" as={Link} to="/catalog/products">
          Catalogue produits
        </Button>
      </Group>
      {loading ? (
        <Text size="sm" c="dimmed">
          Chargement…
        </Text>
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed">
          Aucun produit tagué <code>storefront:featured</code>. Ils alimentent l’accueil boutique et{' '}
          <code>/selection</code>.
        </Text>
      ) : (
        <Paper p="md">
          <Stack gap="xs">
            {items.map((p) => (
              <Group key={p.id} justify="space-between" wrap="nowrap" gap="md">
                <Text size="sm" style={{ minWidth: 0 }}>
                  {p.name}
                </Text>
                <Button type="button" variant="ghost" size="xs" as={Link} to={`/catalog/products/${p.id}`}>
                  Fiche
                </Button>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
